import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSortBy, useTable } from 'react-table'
import { Form, Table } from 'react-bootstrap'
import { format, parseISO } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretDown,
  faCaretUp,
  faSort
} from '@fortawesome/free-solid-svg-icons'
import ReactPaginate from 'react-paginate'
import styled from 'styled-components'
import { PERMISSION } from '../../helpers'

const StyledIcon = styled(FontAwesomeIcon)`
  color: darkGray;
`
const StyledTh = styled.th`
  white-space: nowrap;
`

const UserTable = ({
  users,
  page,
  pageCount,
  onPageChange,
  sort,
  onSort,
  onUpdate
}) => {
  const columns = useMemo(
    () => [
      { Header: 'FirstName', accessor: 'firstName' },
      { Header: 'LastName', accessor: 'lastName' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Username', accessor: 'username' },
      { Header: 'User ID', accessor: 'userId' },
      {
        Header: 'Last Login',
        accessor: 'lastLogin',
        Cell: ({ value }) => {
          try {
            return value ? format(parseISO(value), 'Pp') : null
          } catch (e) {
            console.error('Error parsing lastUpdated', value, e.stack)
            return null
          }
        }
      },
      {
        Header: 'Permission',
        accessor: 'permission',
        Cell: ({ row, value }) => (
          <Form.Control
            as='select'
            value={value}
            aria-label={`permission for ${row.original.username}`}
            onChange={(event) =>
              onUpdate(row.original.id, 'permission', event.target.value)
            }
          >
            <option value={PERMISSION.UNASSIGNED}>Unassigned</option>
            <option value={PERMISSION.VIEW}>Viewer</option>
            <option value={PERMISSION.EDIT}>Editor</option>
            <option value={PERMISSION.ADMIN}>Admin</option>
            {value === PERMISSION.UNASSIGNED || value === PERMISSION.DENIED ? (
              <option value={PERMISSION.DENIED}>Deny Request</option>
            ) : null}
          </Form.Control>
        )
      }
    ],
    [onUpdate]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
    setSortBy
  } = useTable(
    {
      columns,
      data: users,
      initialState: { sortBy: sort },
      manualSortBy: true,
      onUpdate
    },
    useSortBy
  )
  const paginationRef = useRef(null)

  // react-paginate is setting role of UL to 'navigation' (LIs must be in a UL)
  useEffect(() => {
    if (paginationRef.current) {
      const ulElement = paginationRef.current.querySelector('.pagination')
      if (ulElement && ulElement.getAttribute('role') === 'navigation') {
        ulElement.removeAttribute('role')
      }
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSortBy(sort), [sort])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onSort(sortBy), [sortBy])

  return (
    <>
      <Table
        aria-label='user and permission data'
        responsive
        bordered
        hover
        striped
        size='sm'
        className='user-admin-table'
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...groupProps } = headerGroup.getHeaderGroupProps()
            return (
              <tr key={key} {...groupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...headerProps } = column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )
                  return (
                    <StyledTh key={key} {...headerProps}>
                      {column.render('Header')}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon
                            icon={faCaretDown}
                            size='lg'
                            className='ml-1'
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faCaretUp}
                            size='lg'
                            className='ml-1'
                          />
                        )
                      ) : (
                        <StyledIcon icon={faSort} size='lg' className='ml-1' />
                      )}
                    </StyledTh>
                  )
                })}
              </tr>
            )
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            return (
              <tr key={key} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key, ...cellProps } = cell.getCellProps()
                  return (
                    <td key={key} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      <nav aria-label='Pagination' ref={paginationRef}>
        <ReactPaginate
          pageCount={pageCount}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          previousLabel='<'
          nextLabel='>'
          breakClassName='page-item'
          breakLinkClassName='page-link'
          onPageChange={onPageChange}
          forcePage={page}
          containerClassName='pagination justify-content-center'
          pageClassName='page-item'
          pageLinkClassName='page-link'
          activeClassName='active'
          previousClassName='page-item'
          nextClassName='page-item'
          previousLinkClassName='page-link'
          nextLinkClassName='page-link'
        />
      </nav>
    </>
  )
}

UserTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  sort: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      desc: PropTypes.bool
    }).isRequired
  ).isRequired,
  onSort: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export { UserTable }
